import type { LatLon } from "@radar-sim/schema";
import type { AreaData, FerryTemplate, Mark } from "./types.js";
import { RACING_MARKS } from "./solent-racing-marks.js";
import { SOLENT_COASTLINE } from "./solent-coastline.js";
import type { TidalAnomaly } from "./weymouth-portland.js";

/**
 * Solent tidal anomalies — disturbed-water areas at headlands and
 * tidal gates where the local stream produces overfalls or eddies
 * dangerous to small craft. Mirrors the Weymouth/Portland data
 * shape; surfaced via getTidalAnomalies("solent") to the engine and
 * renderer.
 */
export const SOLENT_TIDAL_ANOMALIES: ReadonlyArray<TidalAnomaly> = [
  {
    id: "st-catherines-race",
    name: "St Catherine's Race",
    severity: 0.7,
    // Disturbed water S of St Catherine's Point on a SW-going stream
    // — particularly nasty in F4+ winds against tide.
    polygon: [
      { lat: 50.564, lon: -1.31 },
      { lat: 50.565, lon: -1.288 },
      { lat: 50.55, lon: -1.282 },
      { lat: 50.54, lon: -1.298 },
      { lat: 50.548, lon: -1.314 },
    ],
    // SW-going stream — pushes vessels south-westward off St Cat's.
    drift: { bearingDeg: 225, speedKnots: 2.5 },
  },
  {
    id: "hurst-overfalls",
    name: "Hurst Narrows overfalls",
    severity: 0.6,
    // Tidal stream through the narrows accelerates over the shoal
    // banks NE of the spit, causing standing waves on a spring ebb.
    polygon: [
      { lat: 50.71, lon: -1.555 },
      { lat: 50.715, lon: -1.535 },
      { lat: 50.706, lon: -1.525 },
      { lat: 50.7, lon: -1.545 },
    ],
    // West-going (ebb) stream through the narrows.
    drift: { bearingDeg: 270, speedKnots: 3 },
  },
  {
    id: "bramble-eddy",
    name: "Bramble Bank flood eddy",
    severity: 0.4,
    // The Bramble Bank shoals divide the flood stream and cause a
    // gentle eddy on the south side — important for the racing
    // fleet to know which side gives lift on a flood.
    polygon: [
      { lat: 50.795, lon: -1.32 },
      { lat: 50.798, lon: -1.27 },
      { lat: 50.785, lon: -1.265 },
      { lat: 50.78, lon: -1.31 },
    ],
    // Eddy: a slight east-going set (counter to main flood).
    drift: { bearingDeg: 90, speedKnots: 0.8 },
  },
];

/**
 * Solent area data — coastline polylines from OpenStreetMap (see
 * solent-coastline.ts), marks and lanes hand-curated from chart
 * references (Imray Y20 / Admiralty 2050). Mark coordinates are
 * approximate; this is a teaching tool, not a navigational aid.
 *
 * Coastline convention (see makeIsOnLand and the chart land-fill):
 *   coastline[0] is the OPEN mainland polyline (Hampshire south
 *   coast); coastline[1..] are CLOSED island polygons (IOW, Hayling,
 *   Portsea, smaller islands). Renderers fill ring 0 by extending it
 *   off the top of the chart and fill the rest as-is.
 */

const COASTLINE = SOLENT_COASTLINE;

const DEPTH_CONTOURS = [
  // Approximate 10 m contour skirting the Bramble Bank shoals.
  {
    metres: 10,
    line: [
      { lat: 50.795, lon: -1.32 },
      { lat: 50.79, lon: -1.3 },
      { lat: 50.785, lon: -1.28 },
      { lat: 50.78, lon: -1.26 },
      { lat: 50.78, lon: -1.24 },
      { lat: 50.785, lon: -1.22 },
    ],
  },
];

const MARKS: ReadonlyArray<Mark> = [
  {
    id: "nab-tower",
    name: "Nab Tower",
    position: { lat: 50.6717, lon: -0.9533 },
    type: "tower",
    rcs: 0.95,
    racon: { code: "T" },
    light: {
      character: "Mo(T)15s",
      pattern: { kind: "morse", char: "T", periodSec: 15 },
      color: "W",
      rangeNm: 12,
      heightM: 27,
      fogSignal: "Horn(2)30s",
    },
  },
  {
    id: "bramble-bank-pile",
    name: "Bramble Bank pile",
    position: { lat: 50.79, lon: -1.288 },
    type: "post",
    rcs: 0.1,
    // Bramble pile is unlit in real life — visible at LW only.
  },
  {
    id: "hurst-fort-light",
    name: "Hurst Fort light",
    position: { lat: 50.7058, lon: -1.5519 },
    type: "light",
    rcs: 0.6,
    light: {
      character: "Iso.WRG.4s",
      pattern: { kind: "iso", periodSec: 4 },
      color: "W",
      rangeNm: 14,
      heightM: 23,
      fogSignal: "Bell(1)10s",
    },
  },
  {
    id: "calshot-spit",
    name: "Calshot Spit lightfloat",
    position: { lat: 50.804, lon: -1.305 },
    type: "light",
    rcs: 0.4,
    light: {
      character: "Fl.5s",
      pattern: { kind: "flash", periodSec: 5 },
      color: "W",
      rangeNm: 7,
      heightM: 5,
      fogSignal: "Bell(1)30s",
    },
  },
  {
    // Western Solent channel buoy. Original lat 50.713 sat south of
    // the IOW north coast at this longitude (coast ≈ 50.729 at lon
    // -1.466). 50.745 puts the buoy ~1 nm into the channel where it
    // belongs.
    id: "no2-buoy",
    name: "Solent Bank no.2",
    position: { lat: 50.745, lon: -1.45 },
    type: "buoy",
    rcs: 0.08,
    category: "port-hand",
    light: {
      character: "Fl.R.4s",
      pattern: { kind: "flash", periodSec: 4 },
      color: "R",
      rangeNm: 4,
    },
  },
  {
    // Same fix as no.2 — original (50.72, -1.41) was on the IOW
    // (coast ≈ 50.742 at this longitude). Repositioned just north of
    // the simplified coastline polyline so the buoy paints in water.
    id: "no4-buoy",
    name: "Solent Bank no.4",
    position: { lat: 50.755, lon: -1.4 },
    type: "buoy",
    rcs: 0.08,
    category: "port-hand",
    light: {
      character: "Fl.R.4s",
      pattern: { kind: "flash", periodSec: 4 },
      color: "R",
      rangeNm: 4,
    },
  },
  {
    id: "prince-consort",
    name: "Prince Consort N cardinal",
    position: { lat: 50.7693, lon: -1.291 },
    type: "buoy",
    rcs: 0.12,
    category: "north-cardinal",
    // North cardinal: continuous quick flashing white.
    light: {
      character: "Q.W",
      pattern: { kind: "quick" },
      color: "W",
      rangeNm: 6,
      fogSignal: "Whis",
    },
  },
  {
    // East Bramble East cardinal — marks the eastern end of the
    // Bramble Bank shoal. Q(3)10s is the standard east-cardinal
    // character.
    id: "east-bramble-ecm",
    name: "East Bramble E cardinal",
    position: { lat: 50.7975, lon: -1.265 },
    type: "buoy",
    rcs: 0.12,
    category: "east-cardinal",
    light: {
      character: "Q(3)10s",
      pattern: { kind: "quick-grouped", flashes: 3, periodSec: 10 },
      color: "W",
      rangeNm: 6,
      fogSignal: "Whis",
    },
  },
  {
    // West Bramble West cardinal — marks the western end of the
    // Bramble Bank shoal. Q(9)15s is the standard west-cardinal
    // character.
    id: "west-bramble-wcm",
    name: "West Bramble W cardinal",
    position: { lat: 50.793, lon: -1.323 },
    type: "buoy",
    rcs: 0.12,
    category: "west-cardinal",
    light: {
      character: "Q(9)15s",
      pattern: { kind: "quick-grouped", flashes: 9, periodSec: 15 },
      color: "W",
      rangeNm: 6,
      fogSignal: "Whis",
    },
  },
  {
    // Hill Head SHM — starboard-hand lateral mark, green cone, Fl.G.
    id: "hill-head-shm",
    name: "Hill Head SHM",
    position: { lat: 50.795, lon: -1.235 },
    type: "buoy",
    rcs: 0.08,
    category: "starboard-hand",
    light: {
      character: "Fl.G.4s",
      pattern: { kind: "flash", periodSec: 4 },
      color: "G",
      rangeNm: 4,
    },
  },
  {
    // Cowes racing yellow special-mark — Fl.Y, marks an offshore
    // racing course buoy. Common in the central Solent during the
    // sailing season.
    id: "cowes-special-y",
    name: "Cowes Racing Y special",
    position: { lat: 50.78, lon: -1.27 },
    type: "buoy",
    rcs: 0.05,
    category: "special",
    light: {
      character: "Fl.Y.4s",
      pattern: { kind: "flash", periodSec: 4 },
      color: "Y",
      rangeNm: 3,
    },
  },
  // Common Solent racing marks — used in Round-the-Island, JOG, and
  // weekly Cowes racing. Most carry a yellow flashing light during
  // the sailing season; positions are approximate (chart references
  // rounded to 3 dp lat/lon).
  {
    id: "lepe-spit",
    name: "Lepe Spit S cardinal",
    position: { lat: 50.78, lon: -1.349 },
    type: "buoy",
    rcs: 0.1,
    category: "south-cardinal",
    light: {
      character: "Q(6)+LFl.15s",
      pattern: { kind: "quick-grouped-long", flashes: 6, periodSec: 15 },
      color: "W",
      rangeNm: 6,
    },
  },
  {
    id: "north-ryde-middle",
    name: "North Ryde Middle PHM",
    position: { lat: 50.7415, lon: -1.1455 },
    type: "buoy",
    rcs: 0.08,
    category: "port-hand",
    light: {
      character: "Fl(4)R.20s",
      pattern: { kind: "quick-grouped", flashes: 4, periodSec: 20 },
      color: "R",
      rangeNm: 5,
    },
  },
  {
    id: "mother-bank",
    name: "Mother Bank PHM",
    position: { lat: 50.7565, lon: -1.182 },
    type: "buoy",
    rcs: 0.08,
    category: "port-hand",
    light: {
      character: "Fl.R.3s",
      pattern: { kind: "flash", periodSec: 3 },
      color: "R",
      rangeNm: 5,
    },
  },
  {
    id: "no-mans-land-fort",
    name: "No Man's Land Fort",
    position: { lat: 50.7395, lon: -1.0925 },
    type: "tower",
    rcs: 0.7,
    light: {
      character: "Iso.WR.2s",
      pattern: { kind: "iso", periodSec: 2 },
      color: "W",
      rangeNm: 6,
      heightM: 21,
    },
  },
  {
    id: "horse-sand-fort",
    name: "Horse Sand Fort",
    position: { lat: 50.745, lon: -1.045 },
    type: "tower",
    rcs: 0.7,
    light: {
      character: "Iso.G.2s",
      pattern: { kind: "iso", periodSec: 2 },
      color: "G",
      rangeNm: 6,
      heightM: 21,
    },
  },
  {
    id: "ryde-middle",
    name: "Ryde Middle SCM",
    position: { lat: 50.755, lon: -1.169 },
    type: "buoy",
    rcs: 0.1,
    category: "south-cardinal",
    light: {
      character: "Q(6)+LFl.15s",
      pattern: { kind: "quick-grouped-long", flashes: 6, periodSec: 15 },
      color: "W",
      rangeNm: 6,
    },
  },
  {
    id: "saddle-yacht-club",
    name: "Saddle Patch racing buoy",
    position: { lat: 50.7755, lon: -1.235 },
    type: "buoy",
    rcs: 0.05,
    category: "special",
    light: {
      character: "Fl.Y.4s",
      pattern: { kind: "flash", periodSec: 4 },
      color: "Y",
      rangeNm: 3,
    },
  },
  {
    id: "browndown",
    name: "Browndown SCM",
    position: { lat: 50.785, lon: -1.211 },
    type: "buoy",
    rcs: 0.08,
    category: "south-cardinal",
    light: {
      character: "Q(6)+LFl.15s",
      pattern: { kind: "quick-grouped-long", flashes: 6, periodSec: 15 },
      color: "W",
      rangeNm: 4,
    },
  },
  {
    id: "boulder-cardinal",
    name: "Boulder PHM",
    position: { lat: 50.7235, lon: -1.4705 },
    type: "buoy",
    rcs: 0.08,
    category: "port-hand",
    light: {
      character: "Fl.R.2.5s",
      pattern: { kind: "flash", periodSec: 2.5 },
      color: "R",
      rangeNm: 4,
    },
  },
  {
    id: "yarmouth-pier-head",
    name: "Yarmouth Pier head",
    position: { lat: 50.71, lon: -1.502 },
    type: "light",
    rcs: 0.1,
    light: {
      character: "2.F.R.vert",
      pattern: { kind: "iso", periodSec: 30 },
      color: "R",
      rangeNm: 2,
    },
  },
  // Round-the-Island race / IOW circumnavigation marks. Positioned at
  // their real chart locations rounded to 4 dp.
  {
    id: "needles-light",
    name: "Needles Lighthouse",
    position: { lat: 50.6622, lon: -1.5916 },
    type: "tower",
    rcs: 0.7,
    light: {
      character: "Oc(2)WRG.20s",
      pattern: { kind: "iso", periodSec: 20 },
      color: "W",
      rangeNm: 17,
      heightM: 24,
      fogSignal: "Horn(2)30s",
    },
  },
  {
    id: "sw-shingles",
    name: "SW Shingles PHM",
    position: { lat: 50.6363, lon: -1.6135 },
    type: "buoy",
    rcs: 0.08,
    category: "port-hand",
    light: {
      character: "Fl.R.2.5s",
      pattern: { kind: "flash", periodSec: 2.5 },
      color: "R",
      rangeNm: 4,
    },
  },
  {
    id: "st-catherines-light",
    name: "St Catherine's Lighthouse",
    position: { lat: 50.5755, lon: -1.2975 },
    type: "tower",
    rcs: 0.6,
    light: {
      character: "Fl(3)30s",
      pattern: { kind: "quick-grouped", flashes: 3, periodSec: 30 },
      color: "W",
      rangeNm: 25,
      heightM: 41,
      fogSignal: "Horn(1)30s",
    },
  },
  {
    id: "bembridge-ledge",
    name: "Bembridge Ledge ECM",
    position: { lat: 50.6925, lon: -1.0307 },
    type: "buoy",
    rcs: 0.12,
    category: "east-cardinal",
    light: {
      character: "Q(3)10s",
      pattern: { kind: "quick-grouped", flashes: 3, periodSec: 10 },
      color: "W",
      rangeNm: 6,
    },
  },
  {
    id: "rys-line",
    name: "Royal Yacht Squadron line",
    position: { lat: 50.764, lon: -1.295 },
    type: "post",
    rcs: 0.05,
    category: "special",
  },
];

const LANES: ReadonlyMap<string, ReadonlyArray<LatLon>> = new Map([
  // Western Solent eastbound traffic — main fairway from Hurst → Cowes.
  [
    "western-solent-eastbound",
    [
      { lat: 50.708, lon: -1.55 },
      { lat: 50.715, lon: -1.5 },
      { lat: 50.728, lon: -1.44 },
      { lat: 50.745, lon: -1.38 },
      { lat: 50.755, lon: -1.32 },
      { lat: 50.762, lon: -1.295 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Eastern Solent eastbound — Cowes → Nab Tower exit.
  [
    "eastern-solent-eastbound",
    [
      { lat: 50.762, lon: -1.295 },
      { lat: 50.75, lon: -1.22 },
      { lat: 50.72, lon: -1.13 },
      { lat: 50.7, lon: -1.05 },
      { lat: 50.682, lon: -0.99 },
      { lat: 50.672, lon: -0.953 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Needles Channel inbound — open sea → Hurst.
  [
    "needles-channel-inbound",
    [
      { lat: 50.62, lon: -1.65 },
      { lat: 50.65, lon: -1.6 },
      { lat: 50.68, lon: -1.575 },
      { lat: 50.706, lon: -1.552 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Bramble Bank northern bypass — used by deep-draught traffic.
  [
    "bramble-bank-north",
    [
      { lat: 50.755, lon: -1.32 },
      { lat: 50.79, lon: -1.31 },
      { lat: 50.795, lon: -1.27 },
      { lat: 50.78, lon: -1.22 },
      { lat: 50.755, lon: -1.18 },
    ] as ReadonlyArray<LatLon>,
  ],
]);

const RACON_IDS = new Set(["nab-tower"]);

const FERRY_TEMPLATES: ReadonlyArray<FerryTemplate> = [
  {
    id: "wightlink-lymington-yarmouth",
    name: "Wightlink Lymington–Yarmouth (W. Sky)",
    vesselType: "ferry",
    rcs: 0.9,
    ais: {
      mmsi: "232001234",
      name: "WIGHT SKY",
      class: "A",
      destination: "YARMOUTH",
      callsign: "MAEK7",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 50.7475, lon: -1.534 }, cog: 200, sog: 9 },
      { atSimSeconds: 90, position: { lat: 50.732, lon: -1.526 }, cog: 200, sog: 10 },
      { atSimSeconds: 600, position: { lat: 50.71, lon: -1.51 }, cog: 200, sog: 10 },
      { atSimSeconds: 1500, position: { lat: 50.708, lon: -1.503 }, cog: 200, sog: 4 },
    ],
  },
  {
    id: "wightlink-portsmouth-fishbourne",
    name: "Wightlink Portsmouth–Fishbourne (St Cecilia)",
    vesselType: "ferry",
    rcs: 0.9,
    ais: {
      mmsi: "232007777",
      name: "ST CECILIA",
      class: "A",
      destination: "FISHBOURNE",
      callsign: "MABZ4",
    },
    // Start outside Portsmouth Harbour mouth (lat 50.790 is ~0.4 nm
    // south of the simplified Hampshire coast at this longitude) and
    // end just off Wootton Creek rather than at the Fishbourne dock
    // (the IOW north coast reaches lat 50.737 at lon -1.207, so 50.745
    // gives the simplified coastline ~0.5 nm of slack).
    waypoints: [
      { atSimSeconds: 0, position: { lat: 50.79, lon: -1.108 }, cog: 200, sog: 8 },
      { atSimSeconds: 600, position: { lat: 50.76, lon: -1.18 }, cog: 215, sog: 11 },
      { atSimSeconds: 1500, position: { lat: 50.745, lon: -1.205 }, cog: 240, sog: 4 },
    ],
  },
  {
    // Brittany Ferries Portsmouth–Caen / Cherbourg / St Malo route.
    // ~22,000 GT RoPax; constrained by draught in the Eastern
    // Solent / Spithead deep-water channel and shows the CBD signal
    // (cylinder by day, three vertical reds by night) while in the
    // channel. Outbound from Portsmouth toward the Nab.
    id: "brittany-ferries-portsmouth",
    name: "Brittany Ferries Portsmouth (Mont St Michel)",
    vesselType: "ferry",
    rcs: 0.95,
    state: "constrained-by-draught",
    ais: {
      mmsi: "232006666",
      name: "MONT ST MICHEL",
      class: "A",
      destination: "CAEN",
      callsign: "MABG2",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 50.79, lon: -1.108 }, cog: 130, sog: 12 },
      { atSimSeconds: 600, position: { lat: 50.74, lon: -1.05 }, cog: 130, sog: 16 },
      { atSimSeconds: 1500, position: { lat: 50.69, lon: -0.97 }, cog: 130, sog: 16 },
    ],
  },
  {
    // Wightlink high-speed catamaran service. Carries the standard
    // power-driven lights plus a yellow flashing all-round at night
    // (Rule 23(b)). Day-time identification: high-speed silhouette
    // and visible jet-wash.
    id: "wightlink-fastcat",
    name: "Wightlink FastCat (Wight Ryder)",
    vesselType: "fastcat",
    rcs: 0.55,
    ais: {
      mmsi: "232008888",
      name: "WIGHT RYDER I",
      class: "A",
      destination: "RYDE PIER",
      callsign: "MABF1",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 50.792, lon: -1.108 }, cog: 165, sog: 18 },
      { atSimSeconds: 600, position: { lat: 50.745, lon: -1.16 }, cog: 200, sog: 22 },
      { atSimSeconds: 1500, position: { lat: 50.733, lon: -1.165 }, cog: 215, sog: 6 },
    ],
  },
  {
    id: "red-funnel-southampton-cowes",
    name: "Red Funnel Southampton–East Cowes (Red Eagle)",
    vesselType: "ferry",
    rcs: 0.9,
    ais: {
      mmsi: "232003333",
      name: "RED EAGLE",
      class: "A",
      destination: "EAST COWES",
      callsign: "MAFD3",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 50.895, lon: -1.4 }, cog: 175, sog: 10 },
      { atSimSeconds: 600, position: { lat: 50.83, lon: -1.36 }, cog: 165, sog: 13 },
      { atSimSeconds: 1500, position: { lat: 50.768, lon: -1.295 }, cog: 165, sog: 5 },
    ],
  },
];

export const SOLENT: AreaData = {
  area: "solent",
  boundingBox: {
    // Covers the Solent proper plus the western RTI marks (Poole Bay,
    // Anvil Point), Needles Channel, IOW south coast, and Spithead.
    // Wide enough that the GoAstern racing-mark dataset (157 marks
    // through Poole Bay → Spithead) all fit inside.
    nw: { lat: 50.92, lon: -1.95 },
    se: { lat: 50.5, lon: -0.85 },
  },
  coastline: COASTLINE,
  depthContours: DEPTH_CONTOURS,
  // Hand-curated authoritative marks first (Nab Tower, Bramble Bank,
  // Calshot, Solent Bank, cardinals, RTI course marks…), then the
  // 139-strong GoAstern racing-mark dataset for sport-only buoys.
  marks: [...MARKS, ...RACING_MARKS],
  lanes: LANES,
  raconIds: RACON_IDS,
  ferryTemplates: FERRY_TEMPLATES,
};
